/**
 * Section B — the fixed risk matrix (property + market), same layout every time. A score
 * above the schema's threshold with no (or an inadequate) comment is flagged — that is the
 * compliance rule (see risk_matrix_schema.comment_required_above).
 */
import type { RiskResultRow, RiskSchemaRow } from '../../reviewer/types';
import { PageLink } from './PageLink';
import { Tile } from '../ui';

function ScoreDots({ score }: { score: number | null }) {
  if (score == null) return <span className="muted">No score</span>;
  return (
    <span className="score-dots" aria-label={`Score ${score} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={`score-dot${n <= score ? ' score-dot--filled' : ''}`} />
      ))}
    </span>
  );
}

function RiskGroup({
  title,
  items,
  results,
  checked,
  onJump,
}: {
  title: string;
  items: RiskSchemaRow[];
  results: Map<string, RiskResultRow>;
  checked: boolean;
  onJump: (doc: 'report' | 'loi', page: number) => void;
}) {
  return (
    <Tile className="risk-group">
      <h3 className="checklist-cat__title">{title}</h3>
      <div className="risk-group__rows">
        {items.map((item) => {
          const result = results.get(item.riskKey);
          const needsFlag = result?.status === 'missing_comment' || result?.status === 'comment_inadequate';
          return (
            <div className="risk-row" key={item.riskKey}>
              <div className="risk-row__head">
                <span className="risk-row__label">{item.label}</span>
                {needsFlag ? <span className="mark mark--flag" title="Needs a comment">!</span> : null}
              </div>
              <ScoreDots score={result?.score ?? null} />
              <p className={`risk-row__comment${needsFlag ? ' risk-row__comment--flag' : ''}`}>
                {result?.commentText
                  ? result.commentText
                  : needsFlag
                    ? `Score above ${item.commentRequiredAbove} needs a written comment.`
                    : result
                      ? 'No comment (not required at this score).'
                      : checked
                        ? 'Not scored for this report.'
                        : 'Awaiting the compliance check.'}
              </p>
              {result ? <PageLink page={result.pageRef} doc="report" onJump={onJump} /> : null}
            </div>
          );
        })}
      </div>
    </Tile>
  );
}

interface RiskSectionProps {
  schema: RiskSchemaRow[];
  results: RiskResultRow[];
  checked: boolean;
  onJump: (doc: 'report' | 'loi', page: number) => void;
}

export function RiskSection({ schema, results, checked, onJump }: RiskSectionProps) {
  const byKey = new Map(results.map((r) => [r.riskKey, r]));
  const property = schema.filter((s) => s.riskGroup === 'property');
  const market = schema.filter((s) => s.riskGroup === 'market');

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">Risk matrix</h2>
      </div>
      <div className="risk-grid">
        <RiskGroup title="Property risk" items={property} results={byKey} checked={checked} onJump={onJump} />
        <RiskGroup title="Market risk" items={market} results={byKey} checked={checked} onJump={onJump} />
      </div>
    </section>
  );
}
